/**
 * MegaMenu — horizontal top-nav mega menu for the admin shell.
 *
 * Replaces the vertical collapsible Sidebar. Top-level categories sit in a
 * horizontal bar; opening one drops a multi-column panel of its links. On
 * narrow screens the bar collapses to a hamburger that opens a full-screen
 * drawer with the same groups as an accordion.
 *
 * Preserves the prior Sidebar behavior: role filtering (same FULL/BLOG/SUPPORT
 * gates as the API), the unread-ticket badge, the brand wordmark, and the
 * account chip + sign-out.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  Activity,
  Archive,
  BookOpen,
  Camera,
  ChevronDown,
  ClipboardList,
  CreditCard,
  Database,
  Globe,
  Home,
  Kanban,
  KeyRound,
  LayoutDashboard,
  LibraryBig,
  LifeBuoy,
  LogOut,
  Mail,
  Menu as MenuIcon,
  MessageSquare,
  MessagesSquare,
  Rocket,
  Route as RouteIcon,
  Shield,
  ShieldCheck,
  Tag,
  Trash2,
  Hourglass,
  Inbox,
  UserX,
  Users,
  Users2,
  Handshake,
  X,
} from 'lucide-react';

type AdminRole = 'oa_admin' | 'ak_admin' | 'oa_blog' | 'oa_support' | 'oa_blog_support';
const FULL: AdminRole[] = ['oa_admin', 'ak_admin'];
const BLOG: AdminRole[] = ['oa_admin', 'ak_admin', 'oa_blog', 'oa_blog_support'];
const SUPPORT: AdminRole[] = ['oa_admin', 'ak_admin', 'oa_support', 'oa_blog_support'];

interface NavItem {
  label: string;
  href: string;
  icon: typeof Home;
  roles: AdminRole[];
}
interface NavGroup {
  title: string;
  items: NavItem[];
}

const OVERVIEW: NavItem = { label: 'Overview', href: '/dashboard', icon: LayoutDashboard, roles: FULL };

const groups: NavGroup[] = [
  {
    title: 'People',
    items: [
      { label: 'Teams', href: '/dashboard/teams', icon: Users2, roles: FULL },
      { label: 'Users', href: '/dashboard/developers', icon: Users, roles: FULL },
      { label: 'Partners', href: '/dashboard/partners', icon: Handshake, roles: FULL },
      { label: 'Authorities', href: '/dashboard/authorities', icon: ShieldCheck, roles: FULL },
      { label: 'oakGuard', href: '/dashboard/oakguard', icon: Shield, roles: FULL },
      { label: 'Integration Health', href: '/dashboard/integration-health', icon: Activity, roles: FULL },
    ],
  },
  {
    title: 'Support',
    items: [
      { label: 'Tickets', href: '/dashboard/tickets', icon: LifeBuoy, roles: SUPPORT },
      { label: 'Feedback', href: '/dashboard/feedback', icon: MessageSquare, roles: SUPPORT },
      { label: 'Beech Hub', href: '/dashboard/beech', icon: MessagesSquare, roles: SUPPORT },
      { label: 'Email history', href: '/dashboard/email-history', icon: Inbox, roles: FULL },
      { label: 'Backlog', href: '/dashboard/backlog', icon: Kanban, roles: FULL },
    ],
  },
  {
    title: 'Content',
    items: [
      { label: 'Blog', href: '/dashboard/blog', icon: BookOpen, roles: BLOG },
      { label: 'Recipes', href: '/dashboard/recipes', icon: BookOpen, roles: FULL },
      { label: 'Runbooks', href: '/dashboard/runbooks', icon: LibraryBig, roles: FULL },
      { label: 'Direct Email', href: '/dashboard/direct-email', icon: Mail, roles: FULL },
    ],
  },
  {
    title: 'Billing',
    items: [
      { label: 'Billing', href: '/dashboard/billing', icon: CreditCard, roles: FULL },
      { label: 'Coupons', href: '/dashboard/coupons', icon: Tag, roles: FULL },
      { label: 'Retention', href: '/dashboard/retention', icon: Archive, roles: FULL },
    ],
  },
  {
    title: 'Access',
    items: [
      { label: 'SSO', href: '/dashboard/sso', icon: Shield, roles: FULL },
      { label: 'Reviewer Links', href: '/dashboard/reviewers', icon: KeyRound, roles: FULL },
    ],
  },
  {
    title: 'Platform',
    items: [
      { label: 'Deploy', href: '/dashboard/deploy', icon: Rocket, roles: FULL },
      { label: 'D1 Explorer', href: '/dashboard/d1', icon: Database, roles: FULL },
      { label: 'Activity', href: '/dashboard/activity', icon: Activity, roles: FULL },
      { label: 'REF Trace', href: '/dashboard/ref', icon: RouteIcon, roles: FULL },
      { label: 'Audit log', href: '/dashboard/audit-log', icon: ClipboardList, roles: FULL },
      { label: 'Traffic', href: '/dashboard/traffic', icon: Globe, roles: FULL },
      { label: 'Screenshots', href: '/dashboard/screenshots', icon: Camera, roles: FULL },
      { label: 'Cleanup', href: '/dashboard/cleanup', icon: Trash2, roles: FULL },
      { label: 'Age-off', href: '/dashboard/age-off', icon: Hourglass, roles: FULL },
      { label: 'Account deletion', href: '/dashboard/account-deletion', icon: UserX, roles: FULL },
    ],
  },
];

interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role?: AdminRole;
}

export default function MegaMenu() {
  const { pathname } = useLocation();
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [me, setMe] = useState<AdminUser | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null); // desktop dropdown
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile drawer
  const barRef = useRef<HTMLDivElement>(null);

  const isActive = (href: string) =>
    href === '/dashboard' ? pathname === href : pathname === href || pathname.startsWith(href + '/');

  const activeGroupTitle = useMemo(() => {
    const g = groups.find((grp) => grp.items.some((it) => isActive(it.href)));
    return g?.title ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Close the desktop dropdown + mobile drawer on route change.
  useEffect(() => {
    setOpenGroup(null);
    setDrawerOpen(false);
  }, [pathname]);

  // Close the desktop dropdown on outside click / Escape.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpenGroup(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenGroup(null);
        setDrawerOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    function fetchUnread() {
      fetch('/api/admin/notifications', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) setUnreadTickets(data.unreadCount || 0);
        })
        .catch(() => {});
    }
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.user) setMe(data.user as AdminUser);
      })
      .catch(() => {});
  }, []);

  const visible = (item: NavItem) => !me?.role || item.roles.includes(me.role);
  const groupVisible = (g: NavGroup) => g.items.some(visible);
  const supportUnread =
    unreadTickets > 0 && me?.role && SUPPORT.includes(me.role) ? unreadTickets : 0;

  function initialOf(s: string): string {
    return s.trim().slice(0, 1).toUpperCase() || '?';
  }

  function ItemLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
    const Icon = item.icon;
    const badge = item.label === 'Tickets' && supportUnread > 0;
    return (
      <Link
        to={item.href}
        onClick={onClick}
        className={`mm-item ${isActive(item.href) ? 'active' : ''}`}
      >
        <Icon size={15} />
        <span>{item.label}</span>
        {badge && <span className="mm-badge">{supportUnread > 9 ? '9+' : supportUnread}</span>}
      </Link>
    );
  }

  return (
    <div className="mm-root" ref={barRef}>
      {/* Brand */}
      <Link to="/" className="mm-brand">
        <Home size={15} style={{ color: 'var(--text-tertiary)' }} />
        <span>
          Oak <span style={{ color: 'var(--brand-primary)' }}>&amp; Ash</span>{' '}
          <span className="mm-brand-sub">admin</span>
        </span>
      </Link>

      {/* Mobile hamburger */}
      <button
        type="button"
        className="mm-hamburger"
        aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
        onClick={() => setDrawerOpen((v) => !v)}
      >
        {drawerOpen ? <X size={20} /> : <MenuIcon size={20} />}
        {supportUnread > 0 && !drawerOpen && <span className="mm-dot" />}
      </button>

      {/* Desktop horizontal bar */}
      <nav className="mm-bar">
        {visible(OVERVIEW) && (
          <Link to={OVERVIEW.href} className={`mm-top ${isActive(OVERVIEW.href) ? 'active' : ''}`}>
            {OVERVIEW.label}
          </Link>
        )}
        {groups.filter(groupVisible).map((group) => {
          const opened = openGroup === group.title;
          const hasUnread = group.title === 'Support' && supportUnread > 0;
          return (
            <div className="mm-top-wrap" key={group.title}>
              <button
                type="button"
                className={`mm-top ${activeGroupTitle === group.title ? 'active' : ''}`}
                aria-expanded={opened}
                onClick={() => setOpenGroup(opened ? null : group.title)}
              >
                {group.title}
                {hasUnread && <span className="mm-dot" />}
                <ChevronDown size={13} className={`mm-chevron ${opened ? 'open' : ''}`} />
              </button>
              {opened && (
                <div className="mm-panel">
                  {group.items.filter(visible).map((item) => (
                    <ItemLink key={item.href} item={item} onClick={() => setOpenGroup(null)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Account chip (desktop, right side) */}
      {me && (
        <div className="mm-account">
          <div className="mm-avatar">{initialOf(me.name || me.email)}</div>
          <a href="/auth/logout" title="Sign out" className="mm-signout">
            <LogOut size={14} />
          </a>
        </div>
      )}

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div className="mm-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="mm-drawer">
            {visible(OVERVIEW) && (
              <ItemLink item={OVERVIEW} onClick={() => setDrawerOpen(false)} />
            )}
            {groups.filter(groupVisible).map((group) => (
              <div className="mm-drawer-group" key={group.title}>
                <div className="mm-drawer-title">{group.title}</div>
                {group.items.filter(visible).map((item) => (
                  <ItemLink key={item.href} item={item} onClick={() => setDrawerOpen(false)} />
                ))}
              </div>
            ))}
            {me && (
              <div className="mm-drawer-account">
                <div className="mm-avatar">{initialOf(me.name || me.email)}</div>
                <div className="mm-drawer-account-info">
                  <div className="mm-drawer-name">{me.name || me.email.split('@')[0]}</div>
                  <div className="mm-drawer-email">{me.email}</div>
                </div>
                <a href="/auth/logout" title="Sign out" className="mm-signout">
                  <LogOut size={14} />
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
